/**
 * 會員 Hub — 進入後第一畫面
 *
 * 顯示：等級徽章 + 快捷入口（資料 / 權益人 / 存摺 / 訂單紀錄 / 商品 / 溯源 / 送禮）
 * 6 格快捷入口全部開放（2026-05-29 開放溯源 + 電子送禮）。
 * 2026-07-19 加入「訂單紀錄」→ /orders（會員在 LINE 內查自己的購買訂單與出貨狀態）。
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Feather, Gift, Leaf, Receipt, ShoppingBag, User, Users, type LucideIcon } from "lucide-react";
import { getMe, pushJoinSurvey } from "../api/members";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import TierBadge from "../components/TierBadge";
import { needsProfile, withNext } from "../utils/join";

const QUICK_LINKS: { to: string; label: string; icon: LucideIcon; enabled: boolean; hint?: string }[] = [
  { to: "/profile", label: "會員資料", icon: User, enabled: true },
  { to: "/beneficiaries", label: "權益人", icon: Users, enabled: true },
  { to: "/wallet", label: "燕窩存摺", icon: Feather, enabled: true },
  { to: "/orders", label: "訂單紀錄", icon: Receipt, enabled: true },
  { to: "/products", label: "商品列表", icon: ShoppingBag, enabled: true },
  { to: "/traceability", label: "溯源查詢", icon: Leaf, enabled: true },
  { to: "/gift/new", label: "電子送禮", icon: Gift, enabled: true },
];

export default function Home() {
  const member = useMemberStore((s) => s.member);
  const [surveyBusy, setSurveyBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await getMe();
        useMemberStore.getState().setMember(m);
      } catch {
        // ignore
      }
    })();
  }, []);

  const name = member?.name || member?.display_name || "貴賓";
  const tier = member?.tier || "general";

  // #16 入會偏好問卷:三題在 LINE 聊天室用按鈕作答,這裡只是「重推一次問卷卡」的入口
  // (入會當下已自動推過第一題;會員沒看到 / 滑掉了,點這張卡再送一次)。
  // busy 鎖住請求期間 — 每點一次就是一則 LINE push,不要連點連送。
  async function sendSurvey() {
    if (surveyBusy) return;
    setSurveyBusy(true);
    const { showToast } = useUIStore.getState();
    try {
      const r = await pushJoinSurvey();
      if (r.status === "pushed") showToast("問卷已送到您的 LINE 聊天室", "success");
      else if (r.status === "completed") showToast("您已完成偏好問卷", "info");
      else showToast("請稍後再試", "error");
    } catch {
      showToast("請稍後再試", "error");
    } finally {
      setSurveyBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-8">
        <div className="text-sm opacity-80">霽雲會員</div>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-2xl font-bold">{name} 您好</h1>
          <TierBadge tier={tier} size="md" />
        </div>
      </header>

      <div className="px-5 mt-6">
        {/* 一鍵加入(2026-09-24)的會員還沒留姓名 / 手機 → 溫和提醒,不擋逛;結帳 / 送禮時才真的擋 */}
        {needsProfile(member) && (
          <Link
            to={withNext("/complete-profile", "/home")}
            className="card mb-4 flex items-center justify-between active:opacity-70"
          >
            <div>
              <div className="text-sm font-medium text-ink">補齊姓名與手機</div>
              <div className="text-xs text-muted mt-0.5">結帳與送禮時會需要,現在補好更順暢</div>
            </div>
            <span className="text-primary text-xl">›</span>
          </Link>
        )}
        {/* 偏好問卷答完(survey.completed_at 有值)就不再顯示;member 還沒載入先不顯示,
            免得已答完的人每次進首頁卡片閃一下(同 needsProfile 的「沒載入先不判」) */}
        {member && !member.survey?.completed_at && (
          <button
            type="button"
            onClick={sendSurvey}
            disabled={surveyBusy}
            className="card mb-4 w-full flex items-center justify-between text-left active:opacity-70 disabled:opacity-60"
          >
            <div>
              <div className="text-sm font-medium text-ink">三題偏好問卷</div>
              <div className="text-xs text-muted mt-0.5">約 30 秒,讓顧問更了解您</div>
            </div>
            <span className="text-primary text-xl">›</span>
          </button>
        )}
        <div className="grid grid-cols-3 gap-3">
          {QUICK_LINKS.map((q) =>
            q.enabled ? (
              <Link
                key={q.to}
                to={q.to}
                className="card flex flex-col items-center py-5 active:opacity-70"
              >
                <span className="h-9 flex items-center justify-center">
                  <q.icon size={28} strokeWidth={1.5} className="text-primary" aria-hidden="true" />
                </span>
                <span className="text-sm mt-2 text-ink">{q.label}</span>
              </Link>
            ) : (
              <div
                key={q.to}
                className="card flex flex-col items-center py-5 opacity-40"
                title={q.hint}
              >
                <span className="h-9 flex items-center justify-center">
                  <q.icon size={28} strokeWidth={1.5} className="text-primary" aria-hidden="true" />
                </span>
                <span className="text-sm mt-2 text-ink">{q.label}</span>
                <span className="text-xxs text-muted mt-1">{q.hint}</span>
              </div>
            ),
          )}
        </div>

        <div className="card mt-6">
          <div className="text-sm text-muted">會籍狀態</div>
          <div className="text-base mt-2">
            到期日：
            <span className="text-ink font-medium">
              {member?.membership_end ? String(member.membership_end).slice(0, 10) : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
